<template>
  <BlogArticle
    title="DossierFacile engagé dans la protection de vos données personnelles"
    date="24 octobre 2022"
    hashtags="DossierFacile,RGPD"
  >
    <BlogPostImage :src="image" />

    <h2>Qu'est-ce qu'une donnée personnelle ?</h2>
    <p>
      Une « donnée personnelle » désigne « toute information se rapportant à une personne physique
      identifiée ou identifiable ». Par exemple :
    </p>
    <ul>
      <li>un nom et prénom</li>
      <li>un identifiant (numéro client, numéro de téléphone...)</li>
      <li>une caractéristique (sexe, âge, revenu, état de santé...)</li>
    </ul>
    <p></p>

    <h2>Qu'est-ce qu'un traitement de données personnelles ?</h2>
    <p>
      Un traitement de données personnelles est une opération, ou ensemble d'opérations, portant sur
      des données personnelles, quel que soit le procédé (collecte, enregistrement, organisation,
      conservation, adaptation, modification, extraction, consultation, utilisation, communication
      par transmission ou diffusion ou toute autre forme de mise à disposition, rapprochement). Il
      n'est pas nécessairement informatisé : les fichiers papier sont également concernés et doivent
      être protégés dans les mêmes conditions.
    </p>
    <p>
      Un traitement de données personnelles doit avoir une finalité, c’est-à-dire un but utile et
      immédiatement nécessaire, qui doit bien évidemment être légal et légitime au regard de
      l’activité professionnelle.
    </p>

    <h2>Qu'est-ce que le RGPD ?</h2>
    <p>
      RGPD signifie
      <a href="https://www.cnil.fr/fr/reglement-europeen-protection-donnees">
        « Règlement Général sur la Protection des Données » </a
      >. Il s’agit d’un nouveau règlement européen qui encadre le traitement des données
      personnelles sur le territoire de l’Union européenne. Il s’inscrit dans la continuité de la
      loi française Informatique et Libertés de 1978 et renforce le contrôle par les citoyens de
      l’utilisation qui peut être faite des données les concernant. Il harmonise les règles en
      Europe en offrant un cadre juridique unique aux professionnels. Il permet de développer leurs
      activités numériques au sein de l’UE en se fondant sur la confiance des utilisateurs.
    </p>

    <h2>Qui est concerné par le RGPD ?</h2>
    <p>
      Le RGPD s’applique à toute organisation, publique ou privée, quels que soient sa taille, son
      pays d’implantation et son activité, qui traite des données personnelles pour son compte ou
      non, dès lors qu'elle est établie sur le territoire de l’Union européenne, ou que son activité
      cible directement des résidents européens. Le traitement ou collecte de données pour le compte
      d’une autre entité (entreprise, collectivité, association) implique également des obligations
      spécifiques pour garantir la protection des données confiées.
    </p>

    <h2>Et DossierFacile dans tout ça ?</h2>
    <p>
      La sécurité des données personnelles est une priorité pour nous,
      <span class="fr-text--bold"
        >les opérateurs et l'équipe technique DossierFacile sont les seuls</span
      >
      à accéder à vos données lors de la vérification de vos documents. Nous nous engageons à
      <span class="fr-text--bold">protéger les informations</span> que vous nous fournissez. Les
      comptes locataires et propriétaires sont enregistrés dans un centre de données sécurisé.
    </p>
    <p>
      Cependant, DossierFacile peut transmettre vos données personnelles à des
      <span class="fr-text--bold">sous-traitants</span> notamment pour
      <span class="fr-text--bold">améliorer le traitement automatisé des dossiers</span>. Ces
      sous-traitants sont liés par un
      <span class="fr-text--bold">engagement de confidentialité</span>
      conforme au RGPD et ne peuvent faire un autre usage des données que ce qui leur est demandé
      par DossierFacile.
    </p>
    <p>
      DossierFacile utilise également les données des utilisateurs à des fins statistiques, mais ces
      données sont alors anonymisées. Il ne s’agit donc plus de données personnelles mais de données
      anonymes, ne contenant aucune information personnelle.
    </p>
    <p>
      Vos données personnelles ne seront
      <span class="fr-text--bold">jamais revendues</span> ou exploitées dans un but commercial ou
      publicitaire. Elles ne seront pas non plus communiquées à des tiers sans votre accord exprès.
    </p>

    <h2>Combien de temps mes données sont-elles conservées chez DossierFacile ?</h2>
    <p>
      DossierFacile conserve vos pièces justificatives et données personnelles autres que nom,
      prénom, email, statut familial, code postal tant que votre compte reste actif (c’est-à-dire
      que vous vous connectez). Lorsque votre compte reste inactif plus de
      <span class="fr-text--bold">3 mois</span>, vos pièces sont supprimées et ce conformément au
      <a
        href="https://www.cnil.fr/sites/default/files/atoms/files/referentiel_relatif_aux_traitements_de_donnees_personnelles_mis_en_oeuvre_dans_le_cadre_de_la_gestion_locative.pdf"
        >référentiel relatif à la gestion locative</a
      >
      de la CNIL (Commission nationale de l’informatique et des libertés).
    </p>
    <p>
      Les autres données sont
      <span class="fr-text--bold">conservées pendant 2 ans</span> à compter de la création de votre
      compte. Cela nous permet d’améliorer notre service en mesurant l'impact et la diffusion
      territoriale de DossierFacile mais aussi de mesurer l'évolution du marché du logement :
      vitesse d'attribution des logements, files d'attente dans les zones denses, comparaison de
      l'offre et de la demande…
    </p>
    <p>
      Afin de protéger vos données et vos documents d’une utilisation frauduleuse, nous les
      protégeons en y ajoutant
      <span class="fr-text--bold">un filigrane</span>
      recouvrant chacune des pages du dossier. Ainsi, ils ne sont pas exploitables en l'état. Voir
      <a href="/blog/usurpation-d-identite-en-france-comment-prevenir-agir"
        >notre article de blog</a
      >
      à ce sujet.
    </p>

    <h2>La suppression des documents chez DossierFacile</h2>
    <p>
      Vous disposez d'un droit d'accès, de rectification et de suppression de vos données. Pour
      l'exercer, envoyez-nous un courriel en précisant la date et l'heure précise de création de
      votre compte (comme nous n'enregistrons pas d'éléments nominatifs, seuls ces éléments peuvent
      nous permettre de retrouver votre compte). Vous pouvez également
      <a href="https://aide.dossierfacile.logement.gouv.fr/fr/article/supprimer-son-compte-ccyu3l/"
        >supprimer votre compte directement depuis votre espace personnel DossierFacile</a
      >.
    </p>

    <h2>Où est hébergée la base de données de DossierFacile ?</h2>
    <p>
      La base de données et les documents déposés sur DossierFacile sont hébergés par OVH
      (entreprise française de stockage de données) dans un data center situé à Gravelines.
    </p>

    <h2>En conclusion</h2>
    <p>
      DossierFacile s’engage à la transparence et au respect du RGPD et se conforme aux lois et au
      référentiel de la CNIL.
    </p>
    <p>
      Vous pourrez trouver plus en détail dans notre
      <a
        href="https://aide.dossierfacile.logement.gouv.fr/fr/article/gestion-des-donnees-et-securite-1d3d36d/"
        >documentation</a
      >
      et dans nos
      <a href="/mentions-legales/">mentions légales</a> les obligations auxquelles se conforme notre
      service et ce dans le but de toujours servir au mieux et respecter nos utilisateurs.
    </p>
  </BlogArticle>
</template>

<script setup lang="ts">
import BlogArticle from '@/views/blog/BlogArticle.vue'
import { useSeoMeta } from '@unhead/vue'
import image from '@images/blog-article14.webp'
import BlogPostImage from '@/components/BlogPostImage.vue'

const title = 'DossierFacile engagé dans la protection de vos données personnelles'
const description =
  'Une « donnée personnelle » désigne « toute information se rapportant à une personne physique identifiée ou identifiabe ».'
const siteTitle = import.meta.env.VITE_SITE_TITLE
const seoTitle = `${title} - ${siteTitle}`

useSeoMeta({
  title: title,
  description: description,
  ogTitle: seoTitle,
  ogImage: image,
  ogDescription: description,
  twitterTitle: seoTitle,
  twitterImage: image,
  twitterDescription: description
})
</script>
